/**
 * Advisory Step Position
 *
 * The canonical step-sequence unions and the pure position/denominator
 * derivation the step counter and query-param whitelist import rather than
 * redeclare. No UI-library import and no i18n import — this module returns
 * data for a caller to translate and render, never a translated string,
 * following `advisoryPresentation.ts`'s exact convention.
 *
 * `total` is `null` before a path exists: the denominator should reflect
 * the *chosen* path, and before a choice exists, printing one invents
 * information the user has not supplied yet. This deliberately diverges
 * from the prototype's `seq()` (`flow-app.js:48-51`), which falls through
 * to the customize sequence while `path` is null and therefore shows the
 * customize denominator from step 1.
 *
 * `question` and `goal` are the only two counted steps before the fork,
 * and the fork screen reuses `goal`'s own position ('02'): the fork is the
 * tail of the same decision as goal review, not a distinct step, so the
 * 3-and-4 sequence stays intact with no extra numerator consumed.
 *
 * The no-hardcoded-3-or-4 rule (forbidding a literal iteration cap such as
 * `maxIterations`) does NOT apply to `EXPRESS_STEP_TOTAL` /
 * `CUSTOMIZE_STEP_TOTAL` below — those are step-SEQUENCE totals owned by the
 * client wizard's own navigation shape, never a value the server could move
 * out from under the client, unlike a refinement cap.
 *
 * The company confirmation is NOT a counted step. It is asked on the first
 * turn, the moment the name is read out of the user's question, and is
 * therefore a stage (`awaitingCompanyConfirmation`) rather than a wizard step —
 * it precedes the fork, so no path exists yet to number it against.
 *
 * The customize sequence dropped from five counted steps to four. The
 * backend has no engagement-level criteria concept — `successCriteria` /
 * `inScope` / `outOfScope` live per key-question, never on their own
 * subcollection — so the criteria screen this counter used to name at
 * `'03'` no longer exists. Keeping the denominator at five with a gap in
 * the middle would advertise a step the user can never reach. `keyQuestions`
 * moved up to `'03'` and `evidence`'s customize position moved up to
 * `'04'`; nothing about the express sequence changed.
 *
 * The classified-engagement view is NOT a counted step of its own.
 * Verified against `agent-svc` source: classification runs synchronously
 * inside the confirm turn's own SSE stream — `goalConfirmed` ->
 * `engagement` -> `questions` -> `done`, all on one stream, typically
 * under three seconds — and completes BEFORE the fork screen is ever
 * reachable. There is no second async wait for a step to number; the
 * classified engagement summary renders directly on the fork screen
 * (`AdvisoryForkScreen`) from `useSupervisorTurn()`'s own parsed
 * `engagement` state, narrated by the existing confirm-turn thinking state
 * exactly like every other in-flight turn, never a new gate or screen. An
 * earlier `AdvisoryMatchGate` component, which used to imagine this as a
 * second async "matching" phase reachable only after the fork, is deleted
 * outright. This sequence therefore stays at four counted steps.
 */

/** The two paths a confirmed engagement can fork into. */
export type AdvisoryPath = 'express' | 'customize';

/** The two URL-addressable, customize/express wizard steps (was three). */
export type AdvisoryWizardStep = 'keyQuestions' | 'evidence';

/**
 * All four counted steps in the sequence. `question` and `goal` are
 * stage-derived — never URL params — and always precede any wizard step.
 */
export type AdvisoryCountedStep = 'question' | 'goal' | AdvisoryWizardStep;

/** A derived step position: a zero-padded numerator and an optional total. */
export interface StepPosition {
  position: string;
  total: string | null;
}

/** The express path's total step count, zero-padded to two characters. */
export const EXPRESS_STEP_TOTAL = '03';

/** The customize path's total step count, zero-padded to two characters. */
export const CUSTOMIZE_STEP_TOTAL = '04';

/**
 * Derives a step's position and total from the four-step derivation table
 * (superseding an earlier five-step table), exactly:
 *
 * - `question` -> position `'01'`
 * - `goal` -> position `'02'`
 * - `keyQuestions` -> position `'03'`, only when `path === 'customize'`;
 *   `null` otherwise (not addressable on express, and not addressable with
 *   no path chosen yet)
 * - `evidence` -> position `'04'` on customize, `'03'` on express, `null`
 *   when `path` is `null`
 *
 * `total` is `null` whenever `path` is `null`, taken literally — and
 * otherwise resolves to `EXPRESS_STEP_TOTAL` / `CUSTOMIZE_STEP_TOTAL` for the
 * respective path. A wizard step combined with a path that cannot reach it
 * (e.g. `keyQuestions` + `express`) is not addressable at all, so it returns
 * `null` rather than a partial position — the caller reconciles back to the
 * server-known stage instead of trusting an invalid URL combination.
 *
 * The closing `const exhaustive: never = step;` guard makes a future fifth
 * `AdvisoryCountedStep` member a compile error here rather than a silent
 * fallthrough, mirroring `advisoryPresentation.ts`'s `stageStatusLine`. The
 * union shrinking from three wizard steps to two does not weaken this guard
 * — it is still exhaustive over whatever `AdvisoryCountedStep` currently is.
 */
export function stepPosition(
  step: AdvisoryCountedStep,
  path: AdvisoryPath | null
): StepPosition | null {
  const total =
    path === null ? null : path === 'express' ? EXPRESS_STEP_TOTAL : CUSTOMIZE_STEP_TOTAL;

  switch (step) {
    case 'question':
      return { position: '01', total };
    case 'goal':
      return { position: '02', total };
    case 'keyQuestions':
      return path === 'customize' ? { position: '03', total } : null;
    case 'evidence':
      if (path === 'customize') {
        return { position: '04', total };
      }
      if (path === 'express') {
        return { position: '03', total };
      }
      return null;
    default: {
      const exhaustive: never = step;
      return exhaustive;
    }
  }
}
